import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { detectarDocumento, limparDocumento } from '@profitlens/dominio';
import { db } from '../config/firebase';
import type { CanalComunicacao, Fornecedor, Vinculo } from '../tipos';

export type RascunhoFornecedor = Omit<
  Fornecedor,
  'id' | 'criadoEm' | 'criadoPor' | 'atualizadoEm' | 'atualizadoPor'
>;

export function fornecedorVazio(): RascunhoFornecedor {
  return {
    razaoSocial: '',
    nomeFantasia: null,
    documento: null,
    tipoDocumento: null,
    site: null,
    contatoNome: null,
    email: null,
    telefone: null,
    whatsapp: null,
    canalPreferencial: null,
    horarioContato: null,
    prazoMedioEntregaDias: null,
    condicoesPagamento: null,
    pedidoMinimoCents: null,
    politicaFrete: null,
    regioesAtendidas: [],
    avaliacaoInterna: null,
    observacoes: null,
    ativo: true,
    consentimento: { ativo: false, em: null, canal: null, origem: null, revogadoEm: null },
  };
}

export function useFornecedores() {
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'fornecedores'), orderBy('razaoSocial')),
        (s) => {
          setFornecedores(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Fornecedor));
          setCarregando(false);
        },
        (e) => {
          setErro(e.message);
          setCarregando(false);
        },
      ),
    [],
  );

  return { fornecedores, carregando, erro };
}

/**
 * Salva o fornecedor garantindo unicidade de CPF/CNPJ (RN-030).
 *
 * A unicidade vem do ID do documento em `documentosFornecedor`, e a transação
 * fecha a brecha entre ler e gravar: dois cadastros simultâneos do mesmo CNPJ
 * não passam os dois.
 */
export async function salvarFornecedor(
  id: string | null,
  dados: RascunhoFornecedor,
  usuarioId: string,
): Promise<string> {
  const ref = id ? doc(db, 'fornecedores', id) : doc(collection(db, 'fornecedores'));
  const documento = dados.documento ? limparDocumento(dados.documento) : null;

  if (documento) {
    const d = detectarDocumento(documento);
    if (!d?.valido) throw new Error(d?.motivo ?? 'Documento inválido');
    dados = { ...dados, documento, tipoDocumento: d.tipo };
  }

  await runTransaction(db, async (tx) => {
    if (documento) {
      const refDoc = doc(db, 'documentosFornecedor', documento);
      const atual = await tx.get(refDoc);
      if (atual.exists() && atual.data().fornecedorId !== ref.id) {
        throw new Error('Já existe outro fornecedor com este CPF/CNPJ.');
      }
      tx.set(refDoc, { fornecedorId: ref.id });
    }

    if (id) {
      tx.update(ref, { ...dados, atualizadoEm: serverTimestamp(), atualizadoPor: usuarioId });
    } else {
      tx.set(ref, {
        ...dados,
        criadoEm: serverTimestamp(),
        criadoPor: usuarioId,
        atualizadoEm: null,
        atualizadoPor: null,
      });
    }
  });

  return ref.id;
}

/**
 * Consentimento para comunicação (RN-031).
 *
 * Sem consentimento ativo para o canal, o Hermes não envia mensagem àquele
 * fornecedor — e revogar cancela agendamentos pendentes (RN-032). O registro
 * manual de cotação continua permitido em qualquer caso: consentimento rege
 * comunicação, não anotação.
 */
export async function registrarConsentimento(
  fornecedorId: string,
  canal: CanalComunicacao,
  origem: string,
): Promise<void> {
  await updateDoc(doc(db, 'fornecedores', fornecedorId), {
    consentimento: { ativo: true, em: serverTimestamp(), canal, origem, revogadoEm: null },
  });
}

export async function revogarConsentimento(fornecedorId: string): Promise<void> {
  await updateDoc(doc(db, 'fornecedores', fornecedorId), {
    'consentimento.ativo': false,
    'consentimento.revogadoEm': serverTimestamp(),
  });
}

// ---------- vínculo produto × fornecedor ----------

export const idVinculo = (fornecedorId: string, produtoId: string) => `${fornecedorId}_${produtoId}`;

export function useVinculos(filtro: { fornecedorId?: string; produtoId?: string }) {
  const [vinculos, setVinculos] = useState<Vinculo[]>([]);
  const { fornecedorId, produtoId } = filtro;

  useEffect(() => {
    if (!fornecedorId && !produtoId) {
      setVinculos([]);
      return;
    }
    const campo = fornecedorId ? 'fornecedorId' : 'produtoId';
    const valor = fornecedorId ?? produtoId!;
    return onSnapshot(query(collection(db, 'vinculos'), where(campo, '==', valor)), (s) =>
      setVinculos(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Vinculo)),
    );
  }, [fornecedorId, produtoId]);

  return vinculos;
}

/**
 * Grava o vínculo e, quando o preço muda, registra o valor anterior no
 * histórico (RN-034). O histórico é só de criação: preço passado não se
 * reescreve, e a regra do Firestore recusa update e delete ali.
 */
export async function salvarVinculo(
  fornecedorId: string,
  produtoId: string,
  dados: Partial<Vinculo>,
  usuarioId: string,
): Promise<void> {
  const id = idVinculo(fornecedorId, produtoId);
  const ref = doc(db, 'vinculos', id);
  let precoAnterior: number | null = null;

  await runTransaction(db, async (tx) => {
    const atual = await tx.get(ref);
    precoAnterior = atual.exists() ? ((atual.data().ultimoPrecoCents as number | null) ?? null) : null;

    if (atual.exists()) {
      tx.update(ref, { ...dados, atualizadoEm: serverTimestamp(), atualizadoPor: usuarioId });
    } else {
      tx.set(ref, {
        fornecedorId,
        produtoId,
        codigoFornecedor: null,
        ultimoPrecoCents: null,
        quantidadeMinimaMilli: null,
        prazoEntregaDias: null,
        condicaoPagamento: null,
        disponibilidade: null,
        ativo: true,
        ...dados,
        criadoEm: serverTimestamp(),
        criadoPor: usuarioId,
        atualizadoEm: null,
        atualizadoPor: null,
      });
    }
  });

  const novoPreco = dados.ultimoPrecoCents;
  if (novoPreco !== undefined && novoPreco !== null && novoPreco !== precoAnterior) {
    await setDoc(doc(collection(db, 'vinculos', id, 'precoHist')), {
      precoCents: novoPreco,
      precoAnteriorCents: precoAnterior,
      quantidadeRefMilli: dados.quantidadeMinimaMilli ?? null,
      origem: 'MANUAL',
      vigenteEm: new Date().toISOString().slice(0, 10),
      registradoPor: usuarioId,
      criadoEm: serverTimestamp(),
    });
  }
}

export async function removerVinculo(fornecedorId: string, produtoId: string): Promise<void> {
  await deleteDoc(doc(db, 'vinculos', idVinculo(fornecedorId, produtoId)));
}

export function usePrecoHist(vinculoId: string | null) {
  const [historico, setHistorico] = useState<
    { id: string; precoCents: number; precoAnteriorCents: number | null; vigenteEm: string }[]
  >([]);

  useEffect(() => {
    if (!vinculoId) {
      setHistorico([]);
      return;
    }
    return onSnapshot(
      query(collection(db, 'vinculos', vinculoId, 'precoHist'), orderBy('vigenteEm', 'desc')),
      (s) => setHistorico(s.docs.map((d) => ({ id: d.id, ...d.data() }) as never)),
    );
  }, [vinculoId]);

  return historico;
}
